import React, { useState, useEffect, useCallback } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';

const CampaignContactList = ({ campaignId, campaignStatus }) => {
    const {
        campaignContacts,
        fetchCampaignContacts,
        addContacts,
        uploadContacts,
        deleteContact,
        deleteAllContacts,
        fetchCampaign,
        loading,
        error
    } = useCampaign();

    const [campaign, setCampaign] = useState(null);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [newContact, setNewContact] = useState({ phone: '', name: '', variables: {} });
    const [uploadFile, setUploadFile] = useState(null);
    const [mappingFields, setMappingFields] = useState({
        phone: '',
        name: '',
        variables: {}
    });
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadResult, setUploadResult] = useState(null);
    const [csvHeaders, setCsvHeaders] = useState([]);

    const loadCampaignData = useCallback(async () => {
        try {
            const campaignData = await fetchCampaign(campaignId);
            if (campaignData) {
                console.log("Campaign data received:", campaignData);

                // Extract variables from templateComponents since templateVariables isn't in the response
                const extractedVariables = [];
                const variablesSet = new Set();

                // Process each component to find variables
                if (campaignData.templateComponents && campaignData.templateComponents.length > 0) {
                    campaignData.templateComponents.forEach(component => {
                        if (component.parameters && component.parameters.length > 0) {
                            component.parameters.forEach(param => {
                                // Handle text parameters with placeholders
                                if (param.type === 'text' && param.text) {
                                    // Find all placeholders like {{1}}, {{2}}, etc.
                                    const matches = param.text.match(/{{([^}]+)}}/g) || [];
                                    matches.forEach(match => {
                                        const varName = match.slice(2, -2).trim();
                                        if (!variablesSet.has(varName)) {
                                            variablesSet.add(varName);
                                            extractedVariables.push({
                                                name: varName,
                                                description: `${component.type.toLowerCase()} parameter ${varName}`,
                                                componentType: component.type
                                            });
                                        }
                                    });
                                }

                                // Handle media parameters for headers (image, video, document)
                                if (component.type === 'HEADER' && ['image', 'video', 'document'].includes(param.type)) {
                                    const mediaType = param.type;
                                    if (!variablesSet.has(`header_${mediaType}`)) {
                                        variablesSet.add(`header_${mediaType}`);
                                        extractedVariables.push({
                                            name: `header_${mediaType}`,
                                            description: `Header ${mediaType} URL`,
                                            componentType: 'HEADER',
                                            paramType: mediaType
                                        });
                                    }
                                }
                            });
                        }
                    });
                }

                // Add extracted variables to the campaign data
                const enrichedCampaignData = {
                    ...campaignData,
                    templateVariables: extractedVariables
                };

                setCampaign(enrichedCampaignData);

                if (extractedVariables.length > 0) {
                    const initialVariables = {};
                    extractedVariables.forEach(variable => {
                        initialVariables[variable.name] = '';
                    });
                    setNewContact(prev => ({
                        ...prev,
                        variables: initialVariables
                    }));
                }
            }
        } catch (err) {
            console.error('Error loading campaign data:', err);
        }
    }, [campaignId, fetchCampaign, setNewContact]);

    const loadContacts = useCallback(async () => {
        const params = {
            page,
            limit: 50
        };

        if (statusFilter) {
            params.status = statusFilter;
        }

        await fetchCampaignContacts(campaignId, params);
    }, [campaignId, page, statusFilter, fetchCampaignContacts]);

    useEffect(() => {
        loadContacts();
        loadCampaignData();
    }, [loadContacts, loadCampaignData]);

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setPage(1);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadFile(file);

        try {
            const reader = new FileReader();
            reader.onload = (event) => {
                const content = event.target.result;
                const lines = content.split('\n');
                if (lines.length > 0) {
                    const headers = lines[0].split(',').map(h => h.trim());
                    setCsvHeaders(headers);
                }
            };
            reader.readAsText(file);
        } catch (err) {
            console.error('Error reading file:', err);
        }
    };

    const handleMappingChange = (field, value) => {
        if (field.startsWith('var_')) {
            const varName = field.substring(4);
            setMappingFields(prev => ({
                ...prev,
                variables: {
                    ...prev.variables,
                    [varName]: value
                }
            }));
        } else {
            setMappingFields(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleAddContact = async (e) => {
        e.preventDefault();
        await addContacts(campaignId, [newContact]);

        const initialVariables = {};
        if (campaign && campaign.templateVariables) {
            campaign.templateVariables.forEach(variable => {
                initialVariables[variable.name] = '';
            });
        }

        setNewContact({ phone: '', name: '', variables: initialVariables });
        setShowAddModal(false);
        loadContacts();
    };

    const handleContactInputChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('var_')) {
            const varName = name.substring(4);
            setNewContact(prev => ({
                ...prev,
                variables: {
                    ...prev.variables,
                    [varName]: value
                }
            }));
        } else {
            setNewContact(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleUploadContacts = async (e) => {
        e.preventDefault();

        if (!uploadFile) return;

        const mapping = {
            phone: mappingFields.phone,
            name: mappingFields.name
        };

        if (Object.keys(mappingFields.variables).length > 0) {
            mapping.variables = {};
            for (const [key, value] of Object.entries(mappingFields.variables)) {
                if (value) {
                    mapping.variables[key] = value;
                }
            }
        }

        setUploadProgress(10);

        try {
            const result = await uploadContacts(campaignId, uploadFile, mapping);
            setUploadResult(result);
            setUploadProgress(100);

            setTimeout(() => {
                setShowUploadModal(false);
                setUploadFile(null);
                setMappingFields({ phone: '', name: '', variables: {} });
                setCsvHeaders([]);
                setUploadProgress(0);
                setUploadResult(null);
                loadContacts();
            }, 3000);
        } catch (err) {
            console.error('Upload error:', err);
            setUploadProgress(0);
        }
    };

    const handleDeleteContact = async (contactId) => {
        if (window.confirm('Are you sure you want to delete this contact?')) {
            await deleteContact(campaignId, contactId);
            loadContacts();
        }
    };

    const handleDeleteAllContacts = async () => {
        if (window.confirm('Are you sure you want to delete ALL contacts from this campaign?')) {
            await deleteAllContacts(campaignId);
            loadContacts();
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-gray-200 text-gray-800';
            case 'sent': return 'bg-blue-200 text-blue-800';
            case 'delivered': return 'bg-green-200 text-green-800';
            case 'read': return 'bg-indigo-200 text-indigo-800';
            case 'failed': return 'bg-red-200 text-red-800';
            default: return 'bg-gray-200 text-gray-800';
        }
    };

    return (
        <div>
            <div className="mb-6 flex justify-between items-center">
                <h3 className="text-lg font-medium">Campaign Contacts</h3>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setShowAddModal(true)}
                        disabled={['completed', 'cancelled', 'failed'].includes(campaignStatus)}
                        className={`px-4 py-2 ${['completed', 'cancelled', 'failed'].includes(campaignStatus)
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'} text-white rounded`}
                    >
                        Add Contact
                    </button>
                    <button
                        onClick={() => setShowUploadModal(true)}
                        disabled={['completed', 'cancelled', 'failed'].includes(campaignStatus)}
                        className={`px-4 py-2 ${['completed', 'cancelled', 'failed'].includes(campaignStatus)
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700'} text-white rounded`}
                    >
                        Upload Contacts
                    </button>
                    {campaignContacts.length > 0 && (
                        <button
                            onClick={handleDeleteAllContacts}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                            Delete All
                        </button>
                    )}
                </div>
            </div>

            <div className="mb-4 flex justify-end">
                <div className="w-64">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
                    <select
                        value={statusFilter}
                        onChange={handleStatusChange}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="sent">Sent</option>
                        <option value="delivered">Delivered</option>
                        <option value="read">Read</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
            </div>

            {loading && campaignContacts.length === 0 ? (
                <div className="p-4 text-center">Loading contacts...</div>
            ) : campaignContacts.length === 0 ? (
                <div className="p-8 text-center text-gray-500 border border-gray-200 rounded-md">
                    No contacts found. Add contacts or upload a file to get started.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variables</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {campaignContacts.map((contact) => (
                                <tr key={contact._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {contact.phone}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {contact.name}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="max-w-xs truncate">
                                            {Object.entries(contact.variables || {}).map(([key, value]) => (
                                                <span key={key} className="inline-block mr-2 mb-1 px-2 py-1 bg-gray-100 rounded text-xs">
                                                    {key}: {value}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contact.status)}`}>
                                            {contact.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {contact.batch || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {formatDate(contact.updatedAt)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        {contact.status === 'pending' && (
                                            <button
                                                onClick={() => handleDeleteContact(contact._id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => handlePageChange(page + 1)}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{campaignContacts.length}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                    >
                                        <span className="sr-only">Previous</span>
                                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                        {page}
                                    </span>
                                    <button
                                        onClick={() => handlePageChange(page + 1)}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                    >
                                        <span className="sr-only">Next</span>
                                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-medium mb-4">Add New Contact</h3>

                        <form onSubmit={handleAddContact}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={newContact.phone}
                                        onChange={handleContactInputChange}
                                        className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="+1234567890"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={newContact.name}
                                        onChange={handleContactInputChange}
                                        className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="John Doe"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Template Variables</label>
                                    <div className="space-y-2">
                                        {campaign && campaign.templateVariables && campaign.templateVariables.length > 0 ? (
                                            campaign.templateVariables.map((variable) => (
                                                <div key={variable.name} className="flex flex-col mb-2">
                                                    <label className="text-sm text-gray-600 mb-1">
                                                        {variable.description || variable.name}
                                                        {variable.example && <span className="text-xs text-gray-500 ml-1">(e.g., {variable.example})</span>}
                                                    </label>
                                                    {variable.paramType ? (
                                                        <div className="space-y-2">
                                                            <div className="flex">
                                                                <input
                                                                    type="text"
                                                                    placeholder={`Enter ${variable.paramType} URL (https://example.com/${variable.paramType}.${variable.paramType === 'image' ? 'jpg' : variable.paramType === 'video' ? 'mp4' : 'pdf'})`}
                                                                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                                    name={`var_${variable.name}`}
                                                                    value={newContact.variables[variable.name] || ''}
                                                                    onChange={handleContactInputChange}
                                                                />
                                                            </div>
                                                            {newContact.variables[variable.name] && (
                                                                <div className="mt-2">
                                                                    {variable.paramType === 'image' ? (
                                                                        <img
                                                                            src={newContact.variables[variable.name]}
                                                                            alt="Header Preview"
                                                                            className="h-20 object-contain border border-gray-200 rounded"
                                                                            onError={(e) => {
                                                                                e.target.onerror = null;
                                                                                e.target.src = 'https://placehold.co/100x100?text=Invalid+Image';
                                                                            }}
                                                                        />
                                                                    ) : variable.paramType === 'video' ? (
                                                                        <div className="flex items-center p-2 bg-gray-100 rounded border border-gray-200">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                            </svg>
                                                                            <span className="text-sm">Video URL provided</span>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center p-2 bg-gray-100 rounded border border-gray-200">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                            </svg>
                                                                            <span className="text-sm">Document URL provided</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            placeholder={`Enter ${variable.description || variable.name}`}
                                                            className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                            name={`var_${variable.name}`}
                                                            value={newContact.variables[variable.name] || ''}
                                                            onChange={handleContactInputChange}
                                                        />
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500">No template variables defined.</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Add Contact
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl">
                        <h3 className="text-lg font-medium mb-4">Upload Contacts</h3>

                        {uploadResult ? (
                            <div className="mb-6">
                                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                                    <h4 className="text-green-800 font-medium mb-2">Upload Complete</h4>
                                    <p className="text-green-700">Successfully imported {uploadResult.result.successful} contacts.</p>
                                    <div className="mt-2 text-sm">
                                        <p>Total: {uploadResult.result.total}</p>
                                        <p>Successful: {uploadResult.result.successful}</p>
                                        <p>Failed: {uploadResult.result.failed}</p>
                                        <p>Duplicates: {uploadResult.result.duplicates}</p>
                                    </div>

                                    {uploadResult.result.errors && uploadResult.result.errors.length > 0 && (
                                        <div className="mt-3">
                                            <h5 className="text-red-600 font-medium mb-1">Errors:</h5>
                                            <ul className="text-sm text-red-600 list-disc pl-5">
                                                {uploadResult.result.errors.map((error, index) => (
                                                    <li key={index}>
                                                        {error.phone}: {error.error}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleUploadContacts}>
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">CSV or Excel File *</label>
                                        <input
                                            type="file"
                                            accept=".csv,.xlsx,.xls"
                                            onChange={handleFileChange}
                                            className="block w-full text-sm text-gray-500
                                                file:mr-4 file:py-2 file:px-4
                                                file:rounded-md file:border-0
                                                file:text-sm file:font-semibold
                                                file:bg-blue-50 file:text-blue-700
                                                hover:file:bg-blue-100"
                                            required
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Upload a CSV or Excel file with contact information
                                        </p>
                                    </div>

                                    <div>
                                        <h4 className="font-medium text-sm mb-2">Map File Columns to Contact Fields</h4>

                                        <div className="space-y-3">
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Column *</label>
                                                    <select
                                                        value={mappingFields.phone}
                                                        onChange={(e) => handleMappingChange('phone', e.target.value)}
                                                        className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                        required
                                                    >
                                                        <option value="">Select column</option>
                                                        {csvHeaders.map((header, idx) => (
                                                            <option key={idx} value={header}>{header}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name Column</label>
                                                    <select
                                                        value={mappingFields.name}
                                                        onChange={(e) => handleMappingChange('name', e.target.value)}
                                                        className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="">Select column</option>
                                                        {csvHeaders.map((header, idx) => (
                                                            <option key={idx} value={header}>{header}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {campaign && campaign.templateVariables && campaign.templateVariables.length > 0 && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Template Variables</label>
                                                    <div className="space-y-2">
                                                        {campaign.templateVariables.map((variable) => (
                                                            <div key={variable.name} className="grid grid-cols-2 gap-3">
                                                                <div>
                                                                    <label className="block text-xs text-gray-600">
                                                                        {variable.description || variable.name}
                                                                        {variable.paramType === 'image' && (
                                                                            <span className="block text-xs text-blue-500">Image URL required</span>
                                                                        )}
                                                                    </label>
                                                                </div>
                                                                <div>
                                                                    <select
                                                                        value={mappingFields.variables[variable.name] || ''}
                                                                        onChange={(e) => handleMappingChange(`var_${variable.name}`, e.target.value)}
                                                                        className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                                    >
                                                                        <option value="">Select column</option>
                                                                        {csvHeaders.map((header, idx) => (
                                                                            <option key={idx} value={header}>{header}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Map columns to template variables (e.g., first_name, customer_id)
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {uploadProgress > 0 && (
                                        <div>
                                            <div className="flex justify-between mb-1">
                                                <span className="text-sm font-medium">Uploading...</span>
                                                <span className="text-sm font-medium">{uploadProgress}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className="bg-blue-600 h-2.5 rounded-full"
                                                    style={{ width: `${uploadProgress}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowUploadModal(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!uploadFile || uploadProgress > 0}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-300"
                                    >
                                        Upload
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampaignContactList;